// src/pages/trabajador/TrabajadorDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import WorkerSendCode from '../../components/gameLoyalty/WorkerSendCode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore,
  faUsers,
  faGift,
  faRocket,
  faBullhorn,
  faArrowRight,
  faStar,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';

const TrabajadorDashboard = () => {
  const { trabajadorId } = useParams();
  const [tiendas, setTiendas] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const navigate = useNavigate();

  // Obtener las tiendas asociadas al trabajador
  useEffect(() => {
    const fetchTiendas = async () => {
      try {
        const tiendaDetails = await get(`trabajadores/${trabajadorId}/tiendas`);
        setTiendas(tiendaDetails);
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: error.message,
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    };

    fetchTiendas();
  }, [trabajadorId]);

  // Para cada tienda, obtener la campaña activa (si existe)
  useEffect(() => {
    const fetchActiveCampaigns = async () => {
      try {
        const campaigns = await Promise.all(
          tiendas.map(async (tienda) => {
            const campaign = await get(`game-loyalty/active?tiendaId=${tienda._id}`).catch(err => null);
            return campaign ? { tiendaId: tienda._id, campaign } : null;
          })
        );
        const active = campaigns.filter(c => c !== null);
        setActiveCampaigns(active);
      } catch (error) {
        console.error('Error fetching active campaigns:', error);
      }
    };

    if (tiendas.length > 0) {
      fetchActiveCampaigns();
    }
  }, [tiendas]);

  const handleViewClientes = (tiendaId) => {
    navigate(`/trabajador/${trabajadorId}/tiendas/${tiendaId}/clientes`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faRocket} className="text-2xl" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">Dashboard de Trabajador</h1>
                <p className="text-blue-100 text-lg">Gestiona tus tiendas y clientes de manera eficiente</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faStore} className="text-white text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tiendas Asociadas</p>
                <p className="text-2xl font-bold text-gray-900">{tiendas.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faBullhorn} className="text-white text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Campañas Activas</p>
                <p className="text-2xl font-bold text-gray-900">{activeCampaigns.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-white text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Última Actividad</p>
                <p className="text-2xl font-bold text-gray-900">Hoy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tiendas Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faStore} className="mr-3 text-blue-600" />
              Mis Tiendas
            </h2>
            <div className="text-sm text-gray-500">
              {tiendas.length} tienda{tiendas.length !== 1 ? 's' : ''} asociada{tiendas.length !== 1 ? 's' : ''}
            </div>
          </div>

          {tiendas.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faStore} className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes tiendas asignadas</h3>
              <p className="text-gray-500">Contacta con tu administrador para obtener acceso a tiendas.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tiendas.map((tienda, index) => (
                <div
                  key={tienda._id}
                  className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faStore} className="text-white text-lg" />
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Activa
                        </span>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tienda.nombre}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{tienda.descripcion || 'Sin descripción disponible'}</p>

                    <div className="space-y-3">
                      <button
                        onClick={() => handleViewClientes(tienda._id)}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center group"
                      >
                        <FontAwesomeIcon icon={faUsers} className="mr-2" />
                        Gestionar Clientes
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Campañas Activas Section */}
        {activeCampaigns.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faBullhorn} className="mr-3 text-green-600" />
                Campañas Activas
              </h2>
              <p className="text-gray-600 mt-1">Envía códigos de recompensa a tus clientes</p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {activeCampaigns.map(({ tiendaId, campaign }, index) => (
                  <div
                    key={tiendaId}
                    className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 hover:shadow-md transition-all duration-300"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                          <FontAwesomeIcon icon={faGift} className="text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{campaign.nombre}</h3>
                          <p className="text-sm text-gray-600">
                            {campaign.scope === 'marca'
                              ? `Marca: ${campaign.marca && campaign.marca.nombre ? campaign.marca.nombre : 'Sin nombre'}`
                              : `Tienda: ${campaign.tienda ? campaign.tienda : tiendaId}`
                            }
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FontAwesomeIcon icon={faStar} className="mr-1" />
                        Activa
                      </span>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <WorkerSendCode campaignId={campaign._id} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TrabajadorDashboard;
